import React, { useEffect, useRef, useState } from 'react';
import { DataArray } from './data';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './skills.scss';

gsap.registerPlugin(ScrollTrigger);

const Skills = () => {
    const skillsRef = useRef(null);
    const titleRef = useRef(null);
    const [filter, setFilter] = useState('All');
    const [animationKey, setAnimationKey] = useState(0);

    const filteredSkills = filter === 'All' 
        ? DataArray 
        : DataArray.filter(skill => skill.category === filter);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.from(titleRef.current, {
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: 'top 80%',
                },
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out'
            });

            // Cards stagger animation
            const cards = document.querySelectorAll('.skill-card');
            gsap.fromTo(cards, 
                {
                    y: 100,
                    opacity: 0,
                },
                {
                    scrollTrigger: {
                        trigger: skillsRef.current,
                        start: 'top 70%',
                    },
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.08,
                    ease: 'power3.out'
                }
            );

        }, skillsRef);

        return () => ctx.revert();
    }, [animationKey]);

    useEffect(() => {
        // Trigger animation when filter changes
        setAnimationKey(prev => prev + 1);
    }, [filter]);

    const handleCardMouseMove = (e, card) => {
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const rotateX = (y - centerY) / 10;
        const rotateY = (centerX - x) / 10;

        gsap.to(card, {
            rotationX: rotateX,
            rotationY: rotateY,
            duration: 0.3,
            ease: 'power2.out'
        });
    };

    const handleCardMouseLeave = (card) => {
        if (!card) return;

        gsap.to(card, {
            rotationX: 0,
            rotationY: 0,
            duration: 0.5,
            ease: 'power2.out'
        });
    };

    const categories = ['All', 'Frontend', 'Tools'];

    return (
        <section className="skills" ref={skillsRef}>
            <div className="skills-container">
                <h2 className="section-title" ref={titleRef}>
                    Technical Expertise
                </h2>
                <p className="section-subtitle">
                    Advanced proficiency in modern frontend technologies and tools
                </p>

                <div className="filter-tabs">
                    {categories.map(category => (
                        <button
                            key={category}
                            className={`filter-tab ${filter === category ? 'active' : ''}`}
                            onClick={() => setFilter(category)}
                        >
                            {category}
                            {category !== 'All' && (
                                <span className="count">
                                    {DataArray.filter(s => s.category === category).length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="skills-grid">
                    {filteredSkills.map((skill) => (
                        <div
                            key={skill.id}
                            className="skill-card"
                            onMouseMove={(e) => handleCardMouseMove(e, e.currentTarget)}
                            onMouseLeave={(e) => handleCardMouseLeave(e.currentTarget)}
                        >
                            <div className="skill-card-inner">
                                <div className="skill-header">
                                    <div className="skill-icon">
                                        <img 
                                            src={require(`../../assets/skills/${skill.src}`)} 
                                            alt={skill.name}
                                        />
                                    </div>
                                    <div className="skill-info">
                                        <h3 className="skill-name">{skill.name}</h3>
                                        <p className="skill-experience">{skill.experience}</p>
                                    </div>
                                </div>
                                <div className="skill-proficiency">
                                    <div className="proficiency-label">
                                        <span>Proficiency</span>
                                        <span className="proficiency-value">{skill.proficiency}%</span>
                                    </div>
                                    <div className="proficiency-bar">
                                        <div 
                                            className="proficiency-fill"
                                            style={{ width: `${skill.proficiency}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="skill-category-badge">{skill.category}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Skills;
